// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { DescribeFieldAttribute, DescribeFieldOutput } from 'thehive-sdk';
import { ManageFiltersOutput } from '@Hooks/useFilters';
import FiltersList from '@Organisms/Filter/FiltersList/FiltersList';
import { Footer } from './components/Footer';

import './THFilter.scss';

interface THFilterProps {
    filterConfig: ManageFiltersOutput;
    describeField?: DescribeFieldOutput;
    children?: ReactElement;
    fieldsToExclude?: string[];
    attributeFormatter?: Record<string, Partial<DescribeFieldAttribute>>;
    aggregationWarning?: boolean;
}

export const THFilter = ({
    filterConfig,
    describeField,
    fieldsToExclude,
    attributeFormatter,
    children,
    aggregationWarning = false,
}: THFilterProps): ReactElement | null => {
    const { editFilters, tempFilters, setTempFilters, removeTempFilter } = filterConfig;

    const tempFiltersDefault = tempFilters?.length ? tempFilters : [{}];

    const { attributes = [], initialQuery = 'listCase', label = '' } = describeField || {};

    const List = (
        <FiltersList
            aggregationWarning={aggregationWarning}
            filters={tempFiltersDefault}
            setFilters={setTempFilters}
            describeField={{
                label,
                initialQuery,
                attributes: attributes
                    .filter(({ name }) => !fieldsToExclude?.includes(name))
                    .map((attribute) => ({
                        ...attribute,
                        name: attributeFormatter?.[attribute.name]?.name || attribute.name,
                    })),
            }}
            removeTempFilter={(index) => {
                removeTempFilter(index);
            }}
        />
    );

    const needFilters = !!tempFilters.length || editFilters;

    const element = (children?.type as any).displayName === 'Footer' ? children : null;

    return element ? (
        needFilters ? (
            <div className="th-filter">
                {editFilters && <div className="th-filter__top">{List}</div>}

                {needFilters && React.cloneElement(element, { describeField, filterConfig })}
            </div>
        ) : null
    ) : (
        List
    );
};

THFilter.Footer = Footer;
